import { useEffect, useState } from "react";

import { css, styled } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { mapProjectDependicies } from "../state";
import { useAppDispatch, useAppSelector } from "../state/hook";

import { DependencyLink } from "./dependency-link";
import { EpicRowItem } from "./epic-row-item";
import { RoadmapBar } from "./roadmap-bar";
import { StoryRowItem } from "./story-row-item";

/**
 * A JSX component for the roadmap grid.
 * @returns A roadmap column JSX component.
 */

export const RoadmapGrid = () => {
    const dispatch = useAppDispatch();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const epics = useAppSelector(({ epics }) => epics);
    const [interactive_state, isDemoComplete, dependency_state] =
        useAppSelector(
            ({ interactive_state, isDemoComplete, dependency_state }) => [
                interactive_state,
                isDemoComplete,
                dependency_state
            ]
        );
    const [barElements, setBarElements] = useState(new Array<JSX.Element>());

    useEffect(() => {
        const epicColumnElements = new Array<JSX.Element>();
        const roadmapBars = new Array<JSX.Element>();

        for (const epic of epics) {
            const { id: epicId, name, children, canClickAddButton } = epic;

            epicColumnElements.push(
                <RoadmapCellContainer
                    hideFromRoadmap={false}
                    key={`epic-row-item-${epicId}`}
                    start={1}
                    end={1}
                    row={roadmapBars.length + 1}
                >
                    <EpicRowItem
                        id={epicId}
                        epicIndex={Number(epics.indexOf(epic))}
                        name={name}
                        canClickAddButton={canClickAddButton}
                    />
                </RoadmapCellContainer>
            );

            // this epic does not have a project timeline
            if (children.length === 0) {
                continue;
            }

            const epicStart = 2;
            const epicEnd = 5;

            roadmapBars.push(
                <RoadmapCellContainer
                    hideFromRoadmap={false}
                    key={`roadmap-bar-epic-${epicId}`}
                    start={epicStart}
                    end={epicEnd}
                    row={roadmapBars.length + 1}
                >
                    <RoadmapBar
                        isDemoComplete={isDemoComplete}
                        epicId={epicId}
                        barHeight={27}
                        childrenIds={children.map(
                            (x) => `roadmap-cell-container-${x.id}`
                        )}
                        color={"#BF63F3"}
                    />
                </RoadmapCellContainer>
            );

            // push all epic children with timeline
            for (const story of children) {
                const {
                    id: storyId,
                    name,
                    start,
                    end,
                    hideFromRoadmap,
                    onClickPayloadAction,
                    onMouseDownAndLeavePayloadAction,
                    canAdjust,
                    isVisible
                } = story;

                let onClickProjectTimeline: Function | undefined = undefined;
                if (onClickPayloadAction) {
                    onClickProjectTimeline = () => {
                        createAnalyticsEvent({
                            action: "clicked",
                            actionSubject: "button",
                            actionSubjectId:
                                "interactiveRoadmapSetProjectTimeline",
                            eventType: "ui"
                        }).fire();

                        dispatch(onClickPayloadAction);
                    };
                }

                let onAdjustTimeline: Function | undefined = undefined;
                if (onMouseDownAndLeavePayloadAction) {
                    onAdjustTimeline = () =>
                        dispatch(onMouseDownAndLeavePayloadAction);
                }

                epicColumnElements.push(
                    <RoadmapCellContainer
                        hideFromRoadmap={false}
                        key={`story-row-item-${storyId}`}
                        start={1}
                        end={1}
                        row={roadmapBars.length + 1}
                    >
                        <StoryRowItem
                            id={storyId}
                            name={name}
                            epicIndex={Number(epics.indexOf(epic))}
                            storyIndex={Number(children.indexOf(story))}
                        />
                    </RoadmapCellContainer>
                );

                roadmapBars.push(
                    <RoadmapCellContainer
                        hideFromRoadmap={
                            isDemoComplete ? false : hideFromRoadmap
                        }
                        data-testid={`roadmap-cell-container-${storyId}`}
                        id={`roadmap-cell-container-${storyId}`}
                        key={`roadmap-cell-container-${storyId}`}
                        start={2}
                        end={5}
                        row={roadmapBars.length + 1}
                    >
                        <RoadmapBar
                            epicId={epicId}
                            storyId={storyId}
                            isDemoComplete={isDemoComplete}
                            data-testid={`roadmap-bar-${storyId}`}
                            barHeight={20}
                            onClickProjectTimeline={onClickProjectTimeline}
                            onAdjustProjectTimeline={onAdjustTimeline}
                            canAdjust={canAdjust}
                            isVisible={isVisible}
                            width={((end ?? 2) - (start ?? 1)) * 206}
                            start={start ?? 2}
                        />
                    </RoadmapCellContainer>
                );
            }
        }

        setBarElements(epicColumnElements.concat(roadmapBars));
    }, [interactive_state, epics, isDemoComplete, dependency_state, dispatch]);

    return (
        <div css={jswRoadmapGridContainerStyles}>
            {/* The bar elements on the grid. filter out elements that do not have a start or end value */}
            <DependencyLink
                interactiveState={interactive_state}
                isDemoComplete={isDemoComplete}
                onPress={() => {
                    createAnalyticsEvent({
                        action: "clicked",
                        actionSubject: "button",
                        actionSubjectId:
                            "interactiveRoadmapMapProjectDependencies",
                        eventType: "ui"
                    }).fire();

                    dispatch(mapProjectDependicies());
                }}
                epics={epics}
            />
            {/* The bar elements on the grid. filter out elements that do not have a start or end value */}
            {barElements.filter(
                (element) =>
                    !element.props.hideFromRoadmap &&
                    element.props.start < Number.MAX_VALUE &&
                    element.props.end > Number.MIN_VALUE
            )}
        </div>
    );
};

const RoadmapCellContainer = styled.div<{
    readonly start: number | null;
    readonly end: number | null;
    readonly row: number;
    readonly hideFromRoadmap: boolean | undefined;
}>({
    display: "flex",
    width: "100%",
    height: "100%",
    gridColumn: ({ start, end }) => `${start} / ${end}`,
    gridRow: ({ row }) => `${row} / ${row}`,
    transition: "all 0.3s ease-in",
    alignItems: "center",
    zIndex: 1
});

const jswRoadmapGridContainerStyles = css({
    marginBlockStart: "75px",
    position: "absolute",
    display: "grid",
    gridTemplateColumns: "290px 205px 205px 205px",
    gridTemplateRows: "repeat(12, 35px)",
    gridAutoFlow: "row"
});
